import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MessageCircle, Bell, Check } from "lucide-react";
import { toast } from "sonner";
import { INTAKE_STATUSES, statusColor } from "./adminConstants";
import RepairJobManage from "@/components/RepairJobManage";

export default function AdminIntakesTab({ intakes, setIntakes }) {
  const [manageIntake, setManageIntake] = useState(null);

  const updateIntakeStatus = async (intakeId, status) => {
    try {
      const { data } = await api.patch(`/admin/repair-intakes/${intakeId}`, { status });
      setIntakes((prev) => prev.map((i) => i.id === intakeId ? data : i));
      toast.success(`Intake ${intakeId.slice(-6)} → ${status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const notifySupport = async (intakeId) => {
    // Open the tab synchronously (within the click's user-gesture window) so browsers
    // don't silently block the popup once the async API call resolves. We then
    // navigate this already-open tab to the real wa.me link.
    // NOTE: must NOT pass "noopener" here — that forces window.open() to return null,
    // which would break our ability to navigate this tab once the link is ready.
    const waWindow = window.open("", "_blank");
    try {
      const { data } = await api.post(`/admin/repair-intakes/${intakeId}/notify-support`);
      if (waWindow && !waWindow.closed) {
        waWindow.opener = null; // drop the opener link before navigating cross-origin (same protection as noopener)
        waWindow.location.href = data.wa_link;
      } else {
        toast.error("Your browser blocked the WhatsApp popup. Please allow popups for this site and try again.");
      }
      setIntakes((prev) => prev.map((i) => i.id === intakeId ? { ...i, support_alerted_at: data.entry.sent_at, support_alerted_by: data.entry.admin_name } : i));
      toast.success("Support alert opened in WhatsApp");
    } catch (err) {
      if (waWindow && !waWindow.closed) waWindow.close();
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <>
      <div className="bg-white rounded-3xl border border-zinc-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Device</TableHead>
              <TableHead>Issue</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Received</TableHead>
              <TableHead className="w-24 text-right">Support alert</TableHead>
              <TableHead className="w-24 text-right">WA</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {intakes.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-10">No repair requests yet</TableCell></TableRow>
            )}
            {intakes.map((it) => (
              <TableRow key={it.id} data-testid={`admin-intake-${it.id}`}>
                <TableCell className="font-mono text-xs">{it.id}</TableCell>
                <TableCell className="text-sm">
                  <div className="font-medium text-zinc-900">{it.name}</div>
                  {it.phone && <div className="text-xs text-emerald-700 flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {it.phone}</div>}
                  {it.email && <div className="text-xs text-zinc-500">{it.email}</div>}
                </TableCell>
                <TableCell className="text-sm">
                  {it.device_type && <span className="inline-block mb-1 text-[10px] font-bold uppercase tracking-[0.1em] rounded-full px-2 py-0.5 bg-zinc-100 text-zinc-600">{it.device_type}</span>}
                  <div>{it.device}</div>
                </TableCell>
                <TableCell className="text-sm">
                  <div className="text-zinc-700">{it.issue_type}</div>
                  <div className="text-xs text-zinc-500 max-w-xs truncate mt-0.5" title={it.description}>{it.description}</div>
                </TableCell>
                <TableCell>
                  <Select value={it.status} onValueChange={(v) => updateIntakeStatus(it.id, v)}>
                    <SelectTrigger data-testid={`intake-status-select-${it.id}`} className={`h-8 rounded-full px-3 text-[10px] font-bold uppercase tracking-[0.15em] border-0 ${statusColor(it.status)}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {INTAKE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-sm text-zinc-500">{new Date(it.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={`intake-notify-support-btn-${it.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => notifySupport(it.id)}
                    className={`rounded-full h-8 px-3 text-xs ${it.support_alerted_at ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-brand text-brand"}`}
                    title="Open a pre-filled WhatsApp message to your support number with this inquiry's details"
                  >
                    {it.support_alerted_at ? <Check className="w-3.5 h-3.5 mr-1" /> : <Bell className="w-3.5 h-3.5 mr-1" />}
                    {it.support_alerted_at ? "Alerted" : "Alert support"}
                  </Button>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={`intake-manage-btn-${it.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => setManageIntake(it)}
                    className="rounded-full h-8 px-3 text-xs"
                  >
                    <MessageCircle className="w-3.5 h-3.5 mr-1" />
                    Manage
                    {(it.wa_notifications || []).length > 0 && (
                      <span className="ml-1.5 text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5">
                        {(it.wa_notifications || []).length}
                      </span>
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <RepairJobManage
        intake={manageIntake}
        open={!!manageIntake}
        onOpenChange={(v) => { if (!v) setManageIntake(null); }}
        onUpdated={(u) => {
          setIntakes((prev) => prev.map((i) => i.id === u.id ? u : i));
          setManageIntake(u);
        }}
      />
    </>
  );
}
